import { Search, CreditCard, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { COPY } from "@/content/copy";

const steps = COPY.howItWorks.steps.map((step, index) => ({
  icon: [Search, CreditCard, Zap][index],
  title: step.title,
  description: step.description,
}));

const HowItWorksSection = () => {
  return (
    <section className="py-20 px-4 bg-purple-50" id="how-it-works" data-section="how-it-works">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            {COPY.howItWorks.title}
          </h2>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-flexaura-teal/10 to-flexaura-purple/10 rounded-full px-6 py-3 mt-4">
            <span className="text-lg font-semibold gradient-text">From AED 2,000 to AED 200,000+</span>
            <span className="text-sm text-muted-foreground">• Flexible payment terms • Quick approval</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            // Alternate colors: purple (0,2), teal (1)
            const isPurple = index % 2 === 0;
            const colorClass = isPurple ? 'bg-[hsl(var(--flexaura-purple))]' : 'bg-[hsl(var(--flexaura-teal))]';
            const hoverColorClass = isPurple ? 'hover:text-[hsl(var(--flexaura-purple))]' : 'hover:text-[hsl(var(--flexaura-teal))]';
            
            if (index === 0) {
              return (
                <Link 
                  key={index} 
                  to="/marketplace" 
                  className="text-center group cursor-pointer hover:scale-105 transition-transform"
                >
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 mx-auto ${colorClass} rounded-full flex items-center justify-center glow-effect group-hover:scale-110 smooth-transition`}>
                      <step.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-flexaura-teal to-flexaura-purple opacity-30"></div>
                  </div>
                  <h3 className={`text-2xl font-heading font-semibold mb-4 ${isPurple ? 'text-[hsl(var(--flexaura-purple))]' : 'text-[hsl(var(--flexaura-teal))]'}`}>
                    {step.title}
                    <span className="ml-2 text-sm text-muted-foreground">→</span>
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {step.description}
                  </p>
                  <p className="text-xs text-primary mt-2 font-medium">Click to explore marketplace</p>
                </Link>
              );
            }
            
            return (
              <div key={index} className="text-center group">
                <div className="relative mb-6">
                  <div className={`w-20 h-20 mx-auto ${colorClass} rounded-full flex items-center justify-center glow-effect group-hover:scale-110 smooth-transition`}>
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-flexaura-teal to-flexaura-purple opacity-30"></div>
                  )}
                </div>
                <h3 className={`text-2xl font-heading font-semibold mb-4 ${isPurple ? 'text-[hsl(var(--flexaura-purple))]' : 'text-[hsl(var(--flexaura-teal))]'}`}>
                  {step.title}
                </h3>
                <p className="text-muted-foreground font-body leading-relaxed">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>
        
        {/* Compact Routing Logic Diagram */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl p-8 card-shadow">
            <h3 className="text-xl font-heading font-semibold text-center mb-8">Smart Routing Logic</h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                  <span className="text-sm font-bold text-blue-600">2K-20K</span>
                </div>
                <span className="text-xs font-medium">BNPL</span>
              </div>
              <div className="hidden md:block w-8 h-0.5 bg-gradient-to-r from-flexaura-teal to-flexaura-purple"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
                  <span className="text-sm font-bold text-green-600">20K-100K</span>
                </div>
                <span className="text-xs font-medium">Bank EMI</span>
              </div>
              <div className="hidden md:block w-8 h-0.5 bg-gradient-to-r from-flexaura-teal to-flexaura-purple"></div>
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                  <span className="text-sm font-bold text-purple-600">50K-200K+</span>
                </div>
                <span className="text-xs font-medium">Extended</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-4">
              Amount → Option (automatic routing based on purchase size)
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;